// src/components/admin/AdminDashboard.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import AdminSettingsPanel from './AdminSettingsPanel';
import ContactMessages from './ContactMessages';
import UserManagementPanel from './UserManagementPanel';
import BlogAdminPanel from './BlogAdminPanel';

const AdminDashboard = ({ initialTab = 'users' }) => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [games, setGames] = useState([]);
  const [gamesLoading, setGamesLoading] = useState(false);
  const [gamesError, setGamesError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [adminCheckFailed, setAdminCheckFailed] = useState(false);
  const [themeFilter, setThemeFilter] = useState('');
  const [mechanicFilter, setMechanicFilter] = useState('');
  const [searchFilter, setSearchFilter] = useState('');
  const [allThemes, setAllThemes] = useState([]);
  const [allMechanics, setAllMechanics] = useState([]);
  const [debouncedSearchFilter, setDebouncedSearchFilter] = useState('');
  const searchTimeoutRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  const [activeTab, setActiveTab] = useState(() => {
    // Extract the tab name from the URL path
    const path = location.pathname.split('/');
    if (path.length >= 3) {
      const tabFromUrl = path[2]; // This will be 'users', 'blog', etc.
      if (['users', 'recommendations', 'games', 'messages', 'blog'].includes(tabFromUrl)) {
        return tabFromUrl;
      }
    }
    // Fallback to initialTab if no valid tab in URL
    return initialTab;
  });


  useEffect(() => {
    // Navigate to the appropriate URL when activeTab changes
    navigate(`/admin/${activeTab}`, { replace: true });
  }, [activeTab, navigate]);

  useEffect(() => {
    // If we're on the root admin path, navigate to the active tab
    if (location.pathname === '/admin') {
      navigate(`/admin/${activeTab}`, { replace: true });
    }
  }, [location.pathname, activeTab, navigate]);

  useEffect(() => {
    // Cleanup function to clear the timeout when component unmounts
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  //Generate sitemap

  const generateSitemap = async () => {
    try {
      setLoading(true);
      setMessage({
        type: 'info',
        text: 'Starting sitemap generation. This may take a few minutes...'
      });
      
      const response = await fetch('/api/admin/generate-sitemap', {
        method: 'POST',
        credentials: 'include',
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to start sitemap generation');
      }
      
      // Start polling for status
      const statusCheckInterval = setInterval(async () => {
        try {
          const statusResponse = await fetch('/api/admin/sitemap-status', {
            credentials: 'include',
          });
          
          if (!statusResponse.ok) {
            clearInterval(statusCheckInterval);
            throw new Error('Failed to check sitemap status');
          }
          
          const statusData = await statusResponse.json();
          
          if (statusData.status === 'complete') {
            clearInterval(statusCheckInterval);
            setLoading(false);
            setMessage({
              type: 'success',
              text: `Sitemap generated successfully with ${statusData.totalGames} games and ${statusData.totalStaticPages} static pages.`
            });
          } else if (statusData.status === 'error') {
            clearInterval(statusCheckInterval);
            setLoading(false);
            setMessage({
              type: 'error',
              text: `Error generating sitemap: ${statusData.errorMessage}`
            });
          }
        } catch (err) {
          clearInterval(statusCheckInterval);
          console.error('Error checking sitemap status:', err);
          setLoading(false);
          setMessage({
            type: 'error',
            text: 'Error checking sitemap status'
          });
        }
      }, 5000); // Check every 5 seconds
      
      // Clean up interval on component unmount
      return () => {
        clearInterval(statusCheckInterval);
      };
      
    } catch (err) {
      console.error('Error generating sitemap:', err);
      setLoading(false);
      setMessage({
        type: 'error',
        text: err.message || 'An error occurred while generating sitemap'
      });
    }
  };

  // Function to check sitemap status
  const checkSitemapStatus = async () => {
    try {
      const response = await fetch('/api/admin/sitemap-status', {
        credentials: 'include',
      });
      
      if (!response.ok) {
        throw new Error('Failed to check sitemap status');
      }
      
      const statusData = await response.json();
      
      if (statusData.status === 'complete') {
        setMessage({
          type: 'success',
          text: `Last sitemap generated on ${new Date(statusData.lastGenerated).toLocaleString()} with ${statusData.totalGames} games, ${statusData.totalBlogPosts} blog posts, and ${statusData.totalStaticPages} static pages.`
        });
      } else if (statusData.status === 'error') {
        setMessage({
          type: 'error',
          text: `Error during last generation: ${statusData.errorMessage}`
        });
      } else if (statusData.status === 'processing') {
        setMessage({
          type: 'info',
          text: 'Sitemap generation is currently in progress...'
        });
      } else {
        setMessage({
          type: 'info',
          text: statusData.message || 'No sitemap generation status available'
        });
      }
    } catch (err) {
      console.error('Error checking sitemap status:', err);
      setMessage({
        type: 'error',
        text: err.message || 'An error occurred while checking sitemap status'
      });
    }
  };

  // Function to generate recommendations
  const generateRecommendations = async () => {
    try {
      setLoading(true);
      setMessage(null);
      
      const response = await fetch('/api/admin/generate-recommendations', {
        method: 'POST',
        credentials: 'include',
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setMessage({
          type: 'success',
          text: 'Recommendations generated successfully!',
          timeTaken: data.timeTaken
        });
      } else {
        setMessage({
          type: 'error',
          text: data.error || 'Failed to generate recommendations'
        });
      }
    } catch (err) {
      console.error('Error generating recommendations:', err);
      setMessage({
        type: 'error',
        text: 'An error occurred while generating recommendations'
      });
    } finally {
      setLoading(false);
    }
  };

  // Function to generate recommendations for current user only
const generateUserRecommendations = async () => {
  try {
    setLoading(true);
    setMessage(null);
    
    const response = await fetch('/api/admin/generate-user-recommendations', {
      method: 'POST',
      credentials: 'include',
    });
    
    const data = await response.json();
    
    if (response.ok) {
      setMessage({
        type: 'success',
        text: 'Your recommendations generated successfully!',
        timeTaken: data.timeTaken
      });
    } else {
      setMessage({
        type: 'error',
        text: data.error || 'Failed to generate your recommendations'
      });
    }
  } catch (err) {
    console.error('Error generating user recommendations:', err);
    setMessage({
      type: 'error',
      text: 'An error occurred while generating your recommendations'
    });
  } finally {
    setLoading(false);
  }
};

  // Function to toggle game visibility
  const toggleGameVisibility = async (gameId, currentVisibility) => {
    try {
      const response = await fetch(`/api/admin/games/${gameId}/toggle-visibility`, {
        method: 'POST',
        credentials: 'include',
      });
      
      if (!response.ok) {
        throw new Error('Failed to toggle game visibility');
      }
      
      // Update the games list with the new visibility status
      setGames(prevGames => 
        prevGames.map(game => 
          game.id === gameId 
            ? { ...game, show_in_search: !currentVisibility } 
            : game
        )
      );
      
    } catch (err) {
      console.error('Error toggling game visibility:', err);
      setGamesError('Failed to update game visibility');
    }
  };

  // Function to fetch games data
  const fetchGames = useCallback(async (pageNum = 1) => {
  try {
    setGamesLoading(true);
    setGamesError(null);
    setAdminCheckFailed(false);
    
    // Build the query URL with filters
    let url = `/api/admin/games?page=${pageNum}`;
    if (themeFilter) url += `&theme=${encodeURIComponent(themeFilter)}`;
    if (mechanicFilter) url += `&mechanic=${encodeURIComponent(mechanicFilter)}`;
    if (debouncedSearchFilter) url += `&search=${encodeURIComponent(debouncedSearchFilter)}`;
    
    const response = await fetch(url, {
      credentials: 'include',
    });
    
    if (response.status === 403) {
      setAdminCheckFailed(true);
      throw new Error('Your admin session has expired. Please log out and log in again.');
    }
    
    if (!response.ok) {
      throw new Error('Failed to fetch games');
    }
    
    const data = await response.json();
    
    setGames(data.games);
    setPage(data.page);
    setTotalPages(data.totalPages);
  } catch (err) {
    console.error('Error fetching games:', err);
    setGamesError(err.message);
  } finally {
    setGamesLoading(false);
  }
}, [themeFilter, mechanicFilter, debouncedSearchFilter]);

  const fetchFilters = async () => {
    try {
      
      const response = await fetch('/api/admin/game-filters', {
        credentials: 'include',
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch filters');
      }
      
      const data = await response.json();
      setAllThemes(data.themes || []);
      setAllMechanics(data.mechanics || []);
    } catch (err) {
      console.error('Error fetching filters:', err);
    } finally {

    }
  };

  // Load initial games data
  useEffect(() => {
    if (currentUser?.is_admin) {
      fetchGames();
    }
  }, [currentUser, fetchGames]);

  // Admin load filters
    useEffect(() => {
      if (currentUser?.is_admin && activeTab === 'games') {
        fetchFilters();
      }
    }, [currentUser, activeTab]);


  return (
     <div className="min-h-screen bg-gray-900 py-8 text-gray-100">
      <div className="max-w-6xl mx-auto px-4">
        {!currentUser || !currentUser.is_admin || adminCheckFailed ? (
          <div className="bg-red-900/30 border border-red-700 rounded-lg p-8 text-center">
            <h1 className="text-2xl font-bold text-red-400 mb-4">Access Denied</h1>
            <p className="text-gray-300">
              {adminCheckFailed 
                ? "Your admin session has expired. Please log out and log in again."
                : "You don't have permission to access this area."}
            </p>
          </div>
        ) : (
          <>
            <h1 className="text-3xl font-bold mb-8 text-blue-400">Admin Dashboard</h1>
            
            {/* Tab Navigation */}
            <div className="flex border-b border-gray-700 mb-6">
              <button
                onClick={() => setActiveTab('users')}
                className={`py-3 px-6 font-medium focus:outline-none ${
                  activeTab === 'users'
                    ? 'text-blue-400 border-b-2 border-blue-400'
                    : 'text-gray-400 hover:text-gray-300'
                }`}
              >
                Users
              </button>
              <button
                onClick={() => setActiveTab('recommendations')}
                className={`py-3 px-6 font-medium focus:outline-none ${
                  activeTab === 'recommendations'
                    ? 'text-blue-400 border-b-2 border-blue-400'
                    : 'text-gray-400 hover:text-gray-300'
                }`}
              >
                Recommendations
              </button>
              <button
                onClick={() => setActiveTab('games')}
                className={`py-3 px-6 font-medium focus:outline-none ${
                  activeTab === 'games'
                    ? 'text-blue-400 border-b-2 border-blue-400'
                    : 'text-gray-400 hover:text-gray-300'
                }`}
              >
                Games Database
              </button>
              <button
                onClick={() => setActiveTab('messages')}
                className={`py-3 px-6 font-medium focus:outline-none ${
                  activeTab === 'messages'
                    ? 'text-blue-400 border-b-2 border-blue-400'
                    : 'text-gray-400 hover:text-gray-300'
                }`}
              >
                Messages
              </button>
              <button
                onClick={() => setActiveTab('blog')}
                className={`py-3 px-6 font-medium focus:outline-none ${
                  activeTab === 'blog'
                    ? 'text-blue-400 border-b-2 border-blue-400'
                    : 'text-gray-400 hover:text-gray-300'
                }`}
              >
                Blog
              </button>
              <button
                onClick={() => setActiveTab('functions')}
                className={`py-3 px-6 font-medium focus:outline-none ${
                  activeTab === 'functions'
                    ? 'text-blue-400 border-b-2 border-blue-400'
                    : 'text-gray-400 hover:text-gray-300'
                }`}
              >
                Functions
              </button>
            </div>
            
            {/* Users Tab Content */}
            {activeTab === 'users' && (
              <div className="mb-10">
                <UserManagementPanel />
              </div>
            )}
            
            {/* Recommendations Tab Content */}
            {activeTab === 'recommendations' && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-semibold text-blue-300">Recommendation Settings</h2>
                  <button
                    onClick={generateRecommendations}
                    disabled={loading}
                    className={`px-4 py-2 rounded-md text-white ${
                      loading
                        ? 'bg-blue-700 cursor-not-allowed'
                        : 'bg-blue-600 hover:bg-blue-700'
                    }`}
                  >
                    {loading ? 'Processing...' : 'Generate Recommendations'}
                  </button>
                  <button
                    onClick={generateUserRecommendations}
                    disabled={loading}
                    className={`px-4 py-2 rounded-md text-white ${
                      loading
                        ? 'bg-blue-700 cursor-not-allowed'
                        : 'bg-green-600 hover:bg-green-700'
                    }`}
                  >
                    {loading ? 'Processing...' : 'Generate My Recommendations'}
                  </button>
                </div>
                
                {message && (
                  <div className={`mb-6 p-3 rounded-md ${
                    message.type === 'success' ? 'bg-green-900/30 text-green-300' : 'bg-red-900/30 text-red-300'
                  }`}>
                    {message.text}
                    {message.type === 'success' && message.timeTaken && (
                      <span className="block mt-1 text-sm opacity-80">
                        Completed in {message.timeTaken}
                      </span>
                    )}
                  </div>
                )}
                
                <AdminSettingsPanel />
              </div>
            )}
            
            {/* Games Database Tab Content */}
            {activeTab === 'games' && (
              <div className="mb-8">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-semibold text-blue-300">Games Database</h2>
                  <button
                    onClick={() => fetchGames(1)}
                    className="px-3 py-1 rounded-md bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    Refresh
                  </button>
                </div>
                
                {gamesLoading ? (
                  <div className="text-center py-8">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                    <p className="text-gray-400 mt-2">Loading games...</p>
                  </div>
                ) : gamesError ? (
                  <div className="bg-red-900/30 border border-red-700 rounded-lg p-4 text-red-300">
                    {gamesError}
                  </div>
                ) : (
                  <>
                    {/* Filter controls */}
                    <div className="mb-4 bg-gray-800 rounded-lg border border-gray-700 p-4">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-1">
                            Search by Title
                          </label>
                          <input
                            type="text"
                            value={searchFilter}
                            onChange={(e) => {
                              const value = e.target.value;
                              setSearchFilter(value);
                              
                              // Clear any existing timeout
                              if (searchTimeoutRef.current) {
                                clearTimeout(searchTimeoutRef.current);
                              }
                              
                              // Set a new timeout to update the debounced value
                              searchTimeoutRef.current = setTimeout(() => {
                                setDebouncedSearchFilter(value);
                              }, 500); // 500ms debounce time
                            }}
                            className="w-full p-2 rounded bg-gray-700 border border-gray-600 text-white"
                            placeholder="Enter game title..."
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-1">
                            Filter by Theme
                          </label>
                          <select
                            value={themeFilter}
                            onChange={(e) => setThemeFilter(e.target.value)}
                            className="w-full p-2 rounded bg-gray-700 border border-gray-600 text-white"
                          >
                            <option value="">All Themes</option>
                            {allThemes.map(theme => (
                              <option key={theme} value={theme}>{theme}</option>
                            ))}
                          </select>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-1">
                            Filter by Mechanic
                          </label>
                          <select
                            value={mechanicFilter}
                            onChange={(e) => setMechanicFilter(e.target.value)}
                            className="w-full p-2 rounded bg-gray-700 border border-gray-600 text-white"
                          >
                            <option value="">All Mechanics</option>
                            {allMechanics.map(mechanic => (
                              <option key={mechanic} value={mechanic}>{mechanic}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      
                      <div className="flex justify-end mt-3">
                        <button
                          onClick={() => {
                            setSearchFilter('');
                            setDebouncedSearchFilter('');
                            setThemeFilter('');
                            setMechanicFilter('');
                            fetchGames(1);
                          }}
                          className="px-3 py-1 bg-gray-700 text-gray-300 rounded-md mr-2 hover:bg-gray-600"
                        >
                          Clear Filters
                        </button>
                        <button
                          onClick={() => {
                            setDebouncedSearchFilter(searchFilter);
                            fetchGames(1);
                          }}
                          className="px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                        >
                          Apply Filters
                        </button>
                      </div>
                    </div>

                    <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
                      <div className="overflow-x-auto">
                        <table className="min-w-full">
                          <thead className="bg-gray-700">
                            <tr>
                              <th className="py-3 px-4 text-left text-sm font-semibold text-gray-200">ID</th>
                              <th className="py-3 px-4 text-left text-sm font-semibold text-gray-200">Thumbnail</th>
                              <th className="py-3 px-4 text-left text-sm font-semibold text-gray-200">Title</th>
                              <th className="py-3 px-4 text-left text-sm font-semibold text-gray-200">Themes</th>
                              <th className="py-3 px-4 text-left text-sm font-semibold text-gray-200">Mechanics</th>
                              <th className="py-3 px-4 text-left text-sm font-semibold text-gray-200">Visible</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-700">
                            {games.map(game => (
                              <tr key={game.id} className="hover:bg-gray-750">
                                <td className="py-2 px-4 text-gray-300">{game.id}</td>
                                <td className="py-2 px-4">
                                  {game.thumbnail ? (
                                    <img 
                                      src={`/media/small/${game.thumbnail}`} 
                                      alt={game.title} 
                                      className="w-16 h-16 object-cover rounded"
                                      onError={(e) => {
                                        console.error(`Error loading image: ${game.thumbnail}`);
                                        e.target.src = "/media/placeholder.gif";
                                        e.target.onerror = null; // Prevent infinite loop
                                      }}
                                    />
                                  ) : (
                                    <div className="w-16 h-16 bg-gray-700 rounded flex items-center justify-center">
                                      <span className="text-gray-500 text-xs">No image</span>
                                    </div>
                                  )}
                                </td>
                                <td className="py-2 px-4 text-gray-300">{game.title}</td>
                                <td className="py-2 px-4">
                                  <div className="flex flex-wrap gap-1 max-w-xs">
                                    {Array.isArray(game.themes) && game.themes.length > 0 ? (
                                      game.themes.slice(0, 3).map((theme, index) => (
                                        <span 
                                          key={index} 
                                          className="bg-blue-900/60 text-blue-100 px-2 py-0.5 rounded-full text-xs"
                                        >
                                          {theme}
                                        </span>
                                      ))
                                    ) : (
                                      <span className="text-gray-500 text-xs">No themes</span>
                                    )}
                                    {Array.isArray(game.themes) && game.themes.length > 3 && (
                                      <span className="text-gray-400 text-xs">+{game.themes.length - 3} more</span>
                                    )}
                                  </div>
                                </td>
                                <td className="py-2 px-4">
                                  <div className="flex flex-wrap gap-1 max-w-xs">
                                    {Array.isArray(game.mechanics) && game.mechanics.length > 0 ? (
                                      game.mechanics.slice(0, 3).map((mechanic, index) => (
                                        <span 
                                          key={index} 
                                          className="bg-green-900/60 text-green-100 px-2 py-0.5 rounded-full text-xs"
                                        >
                                          {mechanic}
                                        </span>
                                      ))
                                    ) : (
                                      <span className="text-gray-500 text-xs">No mechanics</span>
                                    )}
                                    {Array.isArray(game.mechanics) && game.mechanics.length > 3 && (
                                      <span className="text-gray-400 text-xs">+{game.mechanics.length - 3} more</span>
                                    )}
                                  </div>
                                </td>
                                <td className="py-2 px-4">
                                  <div className="flex items-center">
                                    <span className={`px-2 py-1 rounded-full text-xs mr-2 ${
                                      game.show_in_search 
                                        ? 'bg-green-900/30 text-green-300' 
                                        : 'bg-red-900/30 text-red-300'
                                    }`}>
                                      {game.show_in_search ? 'Yes' : 'No'}
                                    </span>
                                    <button
                                      onClick={() => toggleGameVisibility(game.id, game.show_in_search)}
                                      className="text-blue-400 hover:text-blue-300 text-sm"
                                    >
                                      Toggle
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {games.length > 0 && (
                        <div className="flex justify-between items-center py-4 px-4 border-t border-gray-700">
                          <div className="text-sm text-gray-400">
                            Showing page {page} of {totalPages}
                          </div>
                          <div className="flex space-x-2">
                            <button
                              onClick={() => fetchGames(page > 1 ? page - 1 : 1)}
                              disabled={page <= 1}
                              className={`px-3 py-1 rounded-md ${
                                page <= 1
                                  ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                              }`}
                            >
                              Previous
                            </button>
                            <button
                              onClick={() => fetchGames(page < totalPages ? page + 1 : totalPages)}
                              disabled={page >= totalPages}
                              className={`px-3 py-1 rounded-md ${
                                page >= totalPages
                                  ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                              }`}
                            >
                              Next
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
            {/* Contact Messages Tab Content */}
              {activeTab === 'messages' && (
                <div className="mb-10">
                  <ContactMessages />
                </div>
              )}

              {/* Blog Tab Content */}
                {activeTab === 'blog' && (
                  <div className="mb-10">
                    <BlogAdminPanel />
                  </div>
                )}

              {/* Functions Tab Content */}
                {activeTab === 'functions' && (
                  <div className="mb-10">
                    <div className="flex justify-between items-center mb-6">
                      <h2 className="text-xl font-semibold text-blue-300">Site Maintenance Functions</h2>
                    </div>
                    
                    {message && (
                      <div className={`mb-6 p-3 rounded-md ${
                        message.type === 'success' ? 'bg-green-900/30 text-green-300' : 'bg-red-900/30 text-red-300'
                      }`}>
                        {message.text}
                      </div>
                    )}
                    
                    <div className="bg-gray-800 rounded-lg border border-gray-700 p-6 mb-6">
                      <h3 className="text-lg font-medium mb-4 text-blue-300">SEO</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="bg-gray-750 p-4 rounded-lg border border-gray-700">
                          <h4 className="text-lg font-medium mb-2 text-blue-300">Sitemap Generator</h4>
                          <p className="text-gray-300 mb-4">
                            Generate a sitemap.xml file that lists all public pages on the site.
                            This helps search engines discover and index your content.
                          </p>
                          <div className="flex space-x-2">
                            <button
                              onClick={generateSitemap}
                              disabled={loading}
                              className={`px-4 py-2 rounded-md text-white ${
                                loading
                                  ? 'bg-blue-700 cursor-not-allowed'
                                  : 'bg-blue-600 hover:bg-blue-700'
                              }`}
                            >
                              {loading ? 'Generating...' : 'Generate Sitemap'}
                            </button>
                            
                            <button
                              onClick={checkSitemapStatus}
                              disabled={loading}
                              className="px-4 py-2 bg-gray-700 text-gray-300 hover:bg-gray-600 rounded-md"
                            >
                              Check Status
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="bg-gray-750 p-4 rounded-lg border border-gray-700">
                      <h4 className="text-lg font-medium mb-2 text-blue-300">Server Status Monitor</h4>
                      <p className="text-gray-300 mb-4">
                        View real-time server metrics including memory usage, response time, and request rate.
                      </p>
                      <a 
                        href="/admin/status" 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-md inline-block"
                      >
                        Open Status Dashboard
                      </a>
                    </div>



                  </div>
                )}


          </>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;